import Vue from 'vue'
import Vuex from 'vuex'
import localforage from 'localforage'

Vue.use(Vuex);

export default new Vuex.Store({
    state: {
        api_url: process.env.VUE_APP_API_URL,

        // Login
        indexedDbAppStore: null,
        indexedDbUserStore: null,
        accessToken: null,
        user: null,
        // Response notification
        responseNotificationToggle: false,
        responseNotificationType: null,
        responseNotificationMessages: null,
        responseNotificationTimeout: null,
        permissionsWithRoles: []
    },
    getters: {
        isAuthenticated(state) {
            return state.accessToken !== null;
        }
    },
    mutations: {
        mutateAppStore(state, payload) {
            state.indexedDbAppStore = payload;
        },
        mutateUserStore(state, payload) {
            state.indexedDbUserStore = payload;
        },
        mutateAccessToken(state, payload) {
            state.accessToken = payload;
        },
        mutateUser(state, payload) {
            state.user = payload;
        },
        mutateResponseNotificationToggle(state, payload) {
            state.responseNotificationToggle = payload;
        },
        mutateResponseNotificationType(state, payload) {
            state.responseNotificationType = payload;
        },
        mutateResponseNotificationMessages(state, payload) {
            state.responseNotificationMessages = payload;
        },
        mutateResponseNotificationTimeout(state, payload) {
            state.responseNotificationTimeout = payload;
        },
        mutatePermissionsWithRoles(state, payload) {
            state.permissionsWithRoles = payload;
        }
    },
    actions: {
        /**
         * Runs before Vue instance initialization.
         */
        async initialSetup({commit, state, dispatch}) {
            // Initialize 'app' and 'user' indexedDB instances.

            commit('mutateAppStore', await localforage.createInstance({storeName: 'app'}));
            commit('mutateUserStore', await localforage.createInstance({storeName: 'user'}));

            // Get access token and user data from indexedDB and save to Vuex instance, (so that
            // the user can remain logged in even if browser is refreshed).
            commit('mutateAccessToken', await state.indexedDbAppStore.getItem('accessToken'));
            commit('mutateUser', await state.indexedDbUserStore.getItem('user'));

            // Set headers
            dispatch('setHeaders');
            dispatch('setAuthHeader', {accessToken: state.accessToken});

            // Get all permissions with their roles from db
            commit('mutatePermissionsWithRoles', await dispatch('fetchData', {
                url: '/api/permissionsWithRoles',
                params: {}
            }));
        },

        /**
         * Checks if given permission exists in permissions. If yes,
         * checks if user has this permission's role assigned to him
         *
         * @param state
         * @param permission_name the name of the permission that we want to check if user has
         * @return {boolean}
         */
        userHasPermission({state}, permission_name) {
            let permission = state.permissionsWithRoles.data.find(permission => permission.name === permission_name);

            return permission ? permission.roles.find(obj => state.user.roles.indexOf(obj.name) > -1) : false;
        },

        /**
         * Set axios request headers
         */
        setHeaders() {
            if (axios.defaults.headers.common['Content-Type'] === undefined) {
                axios.defaults.headers.common['Content-Type'] = `application/json`;
            }

            if (axios.defaults.headers.common['Accept'] === undefined) {
                axios.defaults.headers.common['Accept'] = `application/json`;
            }

            if (axios.defaults.headers.common['X-Requested-With'] === undefined) {
                axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
            }

            if (axios.defaults.headers.common['Content-Encoding'] === undefined) {
                axios.defaults.headers.common['Content-Encoding'] = 'gzip';
            }
        },

        setAuthHeader({}, payload) {
            axios.defaults.headers.common['Authorization'] = payload.accessToken ? `Bearer ${payload.accessToken}` : '';
        },

        async showResponseNotification({commit, state}, data) {
            const response = data.response;

            // Reset notification message.
            await commit('mutateResponseNotificationMessages', '');

            // Set notification type
            await commit('mutateResponseNotificationType', [200, 201].indexOf(response.status) > -1 ? 'success' : 'error');

            // Set notification timeout
            await commit('mutateResponseNotificationTimeout', data.timeout);

            // Set notification message & make notification visible
            await commit('mutateResponseNotificationMessages', response.data.messages);

            await commit('mutateResponseNotificationToggle', true);
        },

        async sendData({dispatch, state}, {method, url, data}) {
            let responseData = [];
            await axios({
                method: method,
                url: `${state.api_url}${url}`,
                data: data
            }).then(response => {
                responseData = response;
                // Display a notification message with the response.
                dispatch('showResponseNotification', {
                    "response": response, "timeout": 3000
                });
            }).catch(error => {
                responseData = error.response;
                dispatch('showResponseNotification', {
                    "response": responseData, "timeout": 5000
                });
            });


            return responseData;
        },

        async fetchData({dispatch, state}, {url, params}) {
            let responseData = [];

            await axios.get(`${state.api_url}${url}`, {params: params})
                .then(response => {
                    responseData = response.data;
                })
                .catch(error => {
                    responseData = error.response;
                    dispatch('showResponseNotification', {
                        "response": error.response, "timeout": 5000
                    });
                });

            return responseData;
        }

    }
})
